import {
  type AccrualLogResponse,
  type AdminEsimGenerateRequestBody,
  type AdminEsimGenerateResponse,
  type CalculateEarnRequest,
  type CalculateEarnResponse,
  type CancelPointsPurchaseRecordRequestBody,
  type CreateCustomRuleRequestBody,
  type CreateCustomRuleResponse,
  type CreateLoyaltyAccountRequest,
  type CreateLoyaltyAccountResponse,
  type CreatePointsPurchaseOfferCampaignRequestBody,
  type CreatePointsPurchaseOfferCampaignResponse,
  type CreatePointsPurchaseOfferRequestBody,
  type CreatePointsPurchaseOfferResponse,
  type CustomRuleSearchParams,
  type CustomRuleSearchResponse,
  type GetLoyaltyAccountResponse,
  type ListPointsPurchaseOfferCampaignsResponse,
  type ListPointsPurchaseOffersAdminResponse,
  type ListPointsPurchaseOffersResponse,
  type ManualAccrualFilterParams,
  type ManualAccrualsResponse,
  type ManualAccrualsRetryRequestBody,
  type ManualAccrualsRetryResponse,
  type PointsAdjustmentRequestBody,
  type PointsAdjustmentResponse,
  type PointsPurchaseOfferDerivedStatus,
  type UpdateCustomRuleRequestBody,
  type UpdateCustomRuleResponse,
  type UpdateManualAccrualStatusRequest,
  type UpdateManualAccrualStatusResponse,
  type UpdatePointsPurchaseOfferCampaignRequestBody,
  type UpdatePointsPurchaseOfferCampaignResponse,
  type UpdatePointsPurchaseOfferRequestBody,
  type UpdatePointsPurchaseOfferResponse,
  type UpdatePointsPurchaseRecordResponse,
} from '~/types/loyaltyContract';

import { request } from './common';

const basePath = () => window.configs.API_HOST + '/api/';

interface RequestOptions {
  signal?: AbortSignal;
}

export async function searchCustomRules(
  params: CustomRuleSearchParams,
  options: RequestOptions = {},
): Promise<CustomRuleSearchResponse> {
  return request(`${basePath()}lux-loyalty/custom-rules/search`, {
    ...options,
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createCustomRule(params: CreateCustomRuleRequestBody): Promise<CreateCustomRuleResponse> {
  return request(`${basePath()}lux-loyalty/custom-rules`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function updateCustomRule(
  customRuleId: string,
  params: UpdateCustomRuleRequestBody,
): Promise<UpdateCustomRuleResponse> {
  return request(`${basePath()}lux-loyalty/custom-rules/${customRuleId}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getManualAccruals(
  params: ManualAccrualFilterParams,
  options: RequestOptions = {},
): Promise<ManualAccrualsResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) {
    queryParams.append('page', params.page.toString());
  }
  if (params.pageSize) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params.status) {
    queryParams.append('status', params.status);
  }
  if (params.customerId) {
    queryParams.append('customerId', params.customerId);
  }
  if (params.orderId) {
    queryParams.append('orderId', params.orderId);
  }
  if (params.itemId) {
    queryParams.append('itemId', params.itemId);
  }
  if (params.productType) {
    queryParams.append('productType', params.productType);
  }

  const queryString = queryParams.toString();
  const url = `${basePath()}lux-loyalty/manual-accruals${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    ...options,
    method: 'GET',
  });
}

export async function retryManualAccruals(
  params: ManualAccrualsRetryRequestBody,
): Promise<ManualAccrualsRetryResponse> {
  return request(`${basePath()}lux-loyalty/manual-accruals`, {
    method: 'PUT',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function calculateEarnPoints(params: CalculateEarnRequest): Promise<CalculateEarnResponse> {
  return request(`${basePath()}lux-loyalty/points/calculate-earn`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getLoyaltyAccount(customerId: string): Promise<GetLoyaltyAccountResponse> {
  return request(`${basePath()}lux-loyalty/account?customerId=${customerId}`, {
    method: 'GET',
  });
}

export async function createLoyaltyAccount(params: CreateLoyaltyAccountRequest): Promise<CreateLoyaltyAccountResponse> {
  return request(`${basePath()}lux-loyalty/account`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

// Address Autocomplete Types
export interface AddressDisplay {
  addressId: string;
  mainText: string;
  secondaryText: string;
  formatted_address: string;
}

export interface AddressComponents {
  unit_number?: string;
  street?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface AddressSuggestion {
  latitude?: number;
  longitude?: number;
  display: AddressDisplay;
  address_components: AddressComponents;
}

export interface AddressAutocompleteResponse {
  status: number;
  message: string;
  result: Array<AddressSuggestion>;
}

export function getAddressSuggestions(input: string, options?: RequestOptions): Promise<AddressAutocompleteResponse> {
  const url = `${basePath()}lux-loyalty/address?input=${encodeURIComponent(input)}`;

  return request(url, { method: 'GET', ...options });
}

/**
 * Fetches the combined accrual log for a specific order item.
 * Returns IBS accrual logs, manual accrual record (if any), and points adjustment records.
 */
export function getAccrualLog(
  orderId: string,
  itemId: string,
  options: RequestOptions = {},
): Promise<AccrualLogResponse> {
  const url = `${basePath()}lux-loyalty/manual-accruals/accrual-log?orderId=${encodeURIComponent(
    orderId,
  )}&itemId=${encodeURIComponent(itemId)}`;

  return request(url, { method: 'GET', ...options });
}

/**
 * Creates a points adjustment (credit or debit) for a customer account.
 * Used by CS to manually allocate points when the automated accrual process has failed.
 */
export function createPointsAdjustment(params: PointsAdjustmentRequestBody): Promise<PointsAdjustmentResponse> {
  return request(`${basePath()}lux-loyalty/points/adjustments`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Updates the status of a manual accrual record, optionally recording who resolved it.
 * Used by CS to mark an item as already given when points were allocated manually via IBS.
 */
export function updateManualAccrualStatus(
  itemId: string,
  params: UpdateManualAccrualStatusRequest,
): Promise<UpdateManualAccrualStatusResponse> {
  return request(`${basePath()}lux-loyalty/manual-accruals/${encodeURIComponent(itemId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Loyalty-admin: upsert eSIM reservation and mint Hubby promocode (skips customer reveal window).
 */
export function generateAdminEsimPromo(params: AdminEsimGenerateRequestBody): Promise<AdminEsimGenerateResponse> {
  return request(`${basePath()}lux-loyalty/admin/esim/generate`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function cancelPointsPurchaseRecord(
  purchaseRecordId: string,
  params: CancelPointsPurchaseRecordRequestBody,
): Promise<UpdatePointsPurchaseRecordResponse> {
  return request(`${basePath()}lux-loyalty/points/purchase-records/${encodeURIComponent(purchaseRecordId)}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function listPointsPurchaseOffersAdmin(
  params?: {
    currency?: string;
    status?: PointsPurchaseOfferDerivedStatus;
  },
  options?: { signal?: AbortSignal },
): Promise<ListPointsPurchaseOffersAdminResponse> {
  const searchParams = new URLSearchParams();
  if (params?.currency) {
    searchParams.set('currency', params.currency);
  }
  if (params?.status) {
    searchParams.set('status', params.status);
  }
  const qs = searchParams.toString();
  return request(`${basePath()}lux-loyalty/admin/points/purchase-offers${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

export function listPointsPurchaseOffers(
  currency: string,
  options?: { signal?: AbortSignal },
): Promise<ListPointsPurchaseOffersResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('currency', currency);
  return request(`${basePath()}lux-loyalty/points/purchase-offers?${searchParams.toString()}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

export function createPointsPurchaseOffer(
  params: CreatePointsPurchaseOfferRequestBody,
): Promise<CreatePointsPurchaseOfferResponse> {
  return request(`${basePath()}lux-loyalty/points/purchase-offers`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function updatePointsPurchaseOffer(
  offerId: string,
  params: UpdatePointsPurchaseOfferRequestBody,
): Promise<UpdatePointsPurchaseOfferResponse> {
  return request(`${basePath()}lux-loyalty/points/purchase-offers/${encodeURIComponent(offerId)}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function listPointsPurchaseOfferCampaigns(): Promise<ListPointsPurchaseOfferCampaignsResponse> {
  return request(`${basePath()}lux-loyalty/points/purchase-offer-campaigns`, {
    method: 'GET',
  });
}

export function createPointsPurchaseOfferCampaign(
  params: CreatePointsPurchaseOfferCampaignRequestBody,
): Promise<CreatePointsPurchaseOfferCampaignResponse> {
  return request(`${basePath()}lux-loyalty/points/purchase-offer-campaigns`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function updatePointsPurchaseOfferCampaign(
  campaignId: string,
  params: UpdatePointsPurchaseOfferCampaignRequestBody,
): Promise<UpdatePointsPurchaseOfferCampaignResponse> {
  return request(`${basePath()}lux-loyalty/points/purchase-offer-campaigns/${encodeURIComponent(campaignId)}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}
